import { Link } from "@inertiajs/react";

import { ActivationGuide } from "@/components/activation-guide";

interface ComparisonRow {
  surface: string;
  when: string;
  auth: string;
  input: string;
  result: string;
  plan: string;
}

interface ToolRow {
  name: string;
  inputs: string;
  outputs: string;
  limits: string;
  plan: string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    surface: "MCP server",
    when: "An agent in Claude or ChatGPT already has the page URL and should call a tool.",
    auth: "OAuth 2.1 with PKCE. Bearer access token. Scope mcp:generate. No API key in the config.",
    input: "A public page URL, or a public image URL for compress and palette.",
    result: "Hosted image URL, a debug report, a CDN URL, or hex colors. Generation can also return image bytes.",
    plan: "generate_og_image needs an active plan and a credit. The other three tools work on a guest connector session.",
  },
  {
    surface: "REST API",
    when: "Your own server should mint an og:image URL while it renders a page.",
    auth: "A JWT in the JSON body field token. You sign it on your server. The API key is the HMAC secret and is not sent.",
    input: "Claims iss (verified domain) and sub (API key prefix), plus template fields such as title.",
    result: "A CDN image URL to put in og:image and twitter:image.",
    plan: "The API allows an active subscription, a past-due subscription, or a trial, and the domain must be verified.",
  },
  {
    surface: "Web generator",
    when: "You have one URL and want to paste it in the browser and download a card.",
    auth: "The form at /new. No MCP config and no JWT.",
    input: "One public page URL.",
    result: "Branded variations you download or publish by hand.",
    plan: "A browser session. It is not the MCP tool, and it does not start from a connector.",
  },
];

const TOOL_ROWS: ToolRow[] = [
  {
    name: "generate_og_image",
    inputs: "Required url. Optional style. Omitted or unknown style uses auto-detect (default).",
    outputs:
      "status, image_url, page_url, title, style. Clients that accept image content also get the preview bytes when they fit in 1.5 MB.",
    limits:
      "Waits up to about 50 seconds for the first preview, then returns. It does not wait for the second variation. 10 new images per hour. Protocol calls and this tool share 60 requests per hour per access token.",
    plan: "Signed-in account with an active plan and at least one credit. A trial is not enough. Guests get plan_required.",
  },
  {
    name: "debug_open_graph",
    inputs: "Required url. Public http(s) only.",
    outputs:
      "status, score, score_label, url, meta_tags, image (url, content_type, byte_size, width, height, aspect_ratio), issues, suggested_meta.",
    limits:
      "About 12 seconds. HTML download stops at 1 MB. Preview image download stops at 4 MB. 30 calls per hour per connector session, inside a shared 40-request free-tool bucket.",
    plan: "Guest connector session. Read-only. Does not spend a credit.",
  },
  {
    name: "compress_image",
    inputs:
      "Required image_url (PNG, JPEG, GIF, WebP, or AVIF). No base64. Optional width and height (default 1200×630, max 4096), format (jpeg, png, webp, avif), quality (1–100, default 82), fit (cover, contain, fill, scale-down, crop, or pad; default cover), target_bytes (10 KB–5 MB).",
    outputs:
      "status, image_url, byte_size, width, height, quality, format, and the source size and dimensions. No image bytes.",
    limits:
      "About 15 seconds. Download stops at 8 MB and 8000 px. 20 calls per hour per connector session, inside the free-tool bucket. When target_bytes is set, quality is searched between 40 and 95.",
    plan: "Guest connector session. Does not spend a credit. Does not use the browser compressor session.",
  },
  {
    name: "extract_color_palette",
    inputs: "Required image_url, same public image rules as compress. Optional limit from 1 to 8, default 5.",
    outputs: "status, colors, swatches, background_color, text_color, contrast_ratio, meets_wcag_aa.",
    limits:
      "About 12 seconds. Same 8 MB and 8000 px cap. 30 calls per hour per connector session, inside the free-tool bucket.",
    plan: "Guest connector session. Read-only. Does not spend a credit.",
  },
];

const REMOTE_CONFIG = `{
  "mcpServers": {
    "og-pilot": {
      "type": "streamable-http",
      "url": "https://ogpilot.com/mcp"
    }
  }
}`;

const CURL_SAMPLE = `curl -X POST "https://ogpilot.com/api/v1/images" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"token":"YOUR_JWT_TOKEN"}'`;

const STYLE_KEYS = [
  "default",
  "minimalistic",
  "modern",
  "vintage",
  "colorful",
  "abstract",
  "geometric",
  "kawaii",
  "flat",
  "comic",
  "anime",
  "van_gogh",
  "watercolor",
  "ballpoint",
  "pencil",
  "pixel_art",
  "retro_game",
  "halloween",
  "thanksgiving",
  "xmas",
].join(", ");

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

const sectionClass = "mx-auto mt-16 max-w-4xl scroll-mt-20 md:mt-20";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
      <pre className="mt-2 overflow-x-auto rounded-md bg-gray-50 p-4 text-xs leading-5 dark:bg-gray-900/60">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function McpServerGuide() {
  return (
    <div id="mcp-server-guide">
      <section className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
          MCP, the API, or the browser
        </h2>
        <p className="text-muted-foreground mt-4 text-base leading-7">
          Use the <strong>OG image MCP server</strong> when an assistant should call OG Pilot. Use the{" "}
          <Link href="/docs" className={linkClass}>
            REST API
          </Link>{" "}
          when your application signs a request on each render. Use the{" "}
          <Link href="/new" className={linkClass}>
            web generator
          </Link>{" "}
          when you have one URL and you will publish the file yourself. The three surfaces do not share a plan check.
        </p>
        <div className="mt-6 overflow-x-auto rounded-lg border bg-white shadow-sm dark:bg-gray-800">
          <table className="w-full min-w-[56rem] border-collapse text-left text-sm text-gray-700 dark:text-gray-300">
            <thead>
              <tr className="border-b bg-gray-50 dark:bg-gray-900/40">
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Surface</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Use it when</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Auth</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Input</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Result</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Plan</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.surface} className="border-b align-top last:border-b-0">
                  <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">{row.surface}</th>
                  <td className="px-3 py-3">{row.when}</td>
                  <td className="px-3 py-3">{row.auth}</td>
                  <td className="px-3 py-3">{row.input}</td>
                  <td className="px-3 py-3">{row.result}</td>
                  <td className="px-3 py-3">{row.plan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground mt-4 text-sm leading-6">
          MCP generation does not burn domain JWT image quota, and it does not open a checkout. Plans are on the{" "}
          <Link href="/#pricing" className={linkClass}>
            pricing section
          </Link>
          . After you publish a tag, re-check the public URL in the{" "}
          <Link href="/open-graph-debugger" className={linkClass}>
            Open Graph debugger
          </Link>
          .
        </p>
      </section>

      <section id="install" className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
          Install the remote server
        </h2>
        <div className="text-muted-foreground mt-4 space-y-4 text-base leading-7">
          <p>
            The only transport is Streamable HTTP. POST JSON-RPC to <code>https://ogpilot.com/mcp</code>. GET does not
            open an SSE stream. There is no stdio binary, no local command, and no API-key header. A client that can
            only launch a process cannot connect.
          </p>
          <p>
            Auth is OAuth 2.1 with PKCE (<code>S256</code>). The client discovers{" "}
            <code>/.well-known/oauth-protected-resource</code> and <code>/.well-known/oauth-authorization-server</code>,
            registers at <code>/oauth/register</code> if it does not already have a client id, then opens{" "}
            <code>/oauth/authorize</code>. Leave the OAuth client id and secret blank unless the client asks you to
            paste values from a failed registration. Supported scopes are <code>mcp:generate</code> and{" "}
            <code>offline_access</code>. All four tools use <code>mcp:generate</code>.
          </p>
          <p>
            On the consent page, choose Allow. A guest connector session can debug, compress, and extract a palette. To
            generate an image, use the account link on that page and approve while signed in to an account with an
            active plan. Access tokens last 1 hour. The token response also includes a refresh token that lasts 90 days.
            Authorization codes last 10 minutes.
          </p>
          <p>
            This block is the remote entry the repo ships. Claude and ChatGPT do not read this file. They use the steps
            under it. Do not add <code>command</code>, <code>args</code>, or a bearer token.
          </p>
        </div>
        <CodeSample label="Remote MCP config" code={REMOTE_CONFIG} />
        <h3 className="mt-8 text-lg font-semibold text-gray-900 dark:text-white">Claude custom connector</h3>
        <ol className="text-muted-foreground mt-3 list-decimal space-y-2 pl-5 text-base leading-7">
          <li>On Free, Pro, and Max, open Customize, then Connectors.</li>
          <li>Add a custom connector and enter https://ogpilot.com/mcp.</li>
          <li>Leave the OAuth client id and secret blank unless Claude asks for values from a failed register.</li>
          <li>Connect, and allow access on the OG Pilot consent page.</li>
          <li>Enable the connector in the chat + menu.</li>
        </ol>
        <p className="text-muted-foreground mt-3 text-base leading-7">
          On Team and Enterprise, an owner adds the custom connector under Organization settings, then Connectors.
          Members then connect from Customize, then Connectors.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-gray-900 dark:text-white">ChatGPT developer mode</h3>
        <ol className="text-muted-foreground mt-3 list-decimal space-y-2 pl-5 text-base leading-7">
          <li>In ChatGPT, open Settings and turn on Developer mode.</li>
          <li>Open Apps or Connectors and create a developer-mode app.</li>
          <li>Name it OG Pilot and set the server URL to https://ogpilot.com/mcp.</li>
          <li>Choose OAuth. Do not submit the app to a public apps portal.</li>
          <li>Allow access on the OG Pilot consent page when it opens.</li>
          <li>Scan tools and enable them. Reconnect if a tool is missing.</li>
        </ol>
        <p className="text-muted-foreground mt-3 text-base leading-7">
          Developer mode is for testing a custom MCP server. The settings label moves. Look under Security, Advanced, or
          workspace permissions. <code>tools/list</code> does not push changes (<code>listChanged</code> is false).
          Reconnect after a tool update.
        </p>
      </section>

      <section id="tools" className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">Tool reference</h2>
        <p className="text-muted-foreground mt-4 text-base leading-7">
          The server exposes these four tools and no others. Private, link-local, and metadata addresses are rejected. A
          failed tool call is still a JSON-RPC result with <code>isError: true</code> and{" "}
          <code>structuredContent.error_code</code>. It is not an HTTP 500.
        </p>
        <div className="mt-6 overflow-x-auto rounded-lg border bg-white shadow-sm dark:bg-gray-800">
          <table className="w-full min-w-[56rem] border-collapse text-left text-sm text-gray-700 dark:text-gray-300">
            <thead>
              <tr className="border-b bg-gray-50 dark:bg-gray-900/40">
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Tool</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Inputs</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Outputs</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Limits</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Plan</th>
              </tr>
            </thead>
            <tbody>
              {TOOL_ROWS.map((row) => (
                <tr key={row.name} className="border-b align-top last:border-b-0">
                  <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">
                    <code>{row.name}</code>
                  </th>
                  <td className="px-3 py-3">{row.inputs}</td>
                  <td className="px-3 py-3">{row.outputs}</td>
                  <td className="px-3 py-3">{row.limits}</td>
                  <td className="px-3 py-3">{row.plan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground mt-4 text-sm leading-6">
          Style keys for <code>generate_og_image</code>: {STYLE_KEYS}. <code>default</code> is auto-detect. Each new
          image reserves one credit. Retrying the same URL and style within 30 minutes reuses a generation that is
          already in flight or finished, instead of reserving another credit. The browser versions of the free tools are
          the{" "}
          <Link href="/open-graph-debugger" className={linkClass}>
            debugger
          </Link>
          , the{" "}
          <Link href="/image-compressor" className={linkClass}>
            image compressor
          </Link>
          , and the{" "}
          <Link href="/image-color-palette-extractor" className={linkClass}>
            palette extractor
          </Link>
          .
        </p>
      </section>

      <section className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">Agent workflows</h2>
        <div className="text-muted-foreground mt-4 space-y-4 text-base leading-7">
          <p>
            The server takes one URL per call. It does not clone a repository or walk a sitemap. The agent loops. Ask it
            to call the tool, then put <code>image_url</code> in the meta tag yourself or in the repo it can edit.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Generate a card for each post.</strong> Give the agent the
            public URLs. It should call <code>generate_og_image</code> once per URL. A timeout means the first preview
            was not stored yet. The same URL can be retried inside 30 minutes without a second credit.
          </p>
        </div>
        <CodeSample
          label="Example prompt: posts"
          code={`Use the OG Pilot MCP server. For each of these public URLs, call generate_og_image and give me the image_url:\nhttps://example.com/blog/launch\nhttps://example.com/blog/pricing\nIf a call returns timeout, retry that same URL once. If it returns plan_required, stop and tell me to pick a plan.`}
        />
        <p className="text-muted-foreground mt-6 text-base leading-7">
          <strong className="text-gray-900 dark:text-white">Debug a LinkedIn preview, then fix the image.</strong>{" "}
          <code>debug_open_graph</code> reads the live tags. It does not clear LinkedIn&apos;s cache. If the image is
          the weak part, <code>compress_image</code> can resize a public image URL to 1200×630, or{" "}
          <code>generate_og_image</code> can draw a new card. Then run the same URL in the{" "}
          <Link href="/open-graph-debugger" className={linkClass}>
            debugger
          </Link>
          .
        </p>
        <CodeSample
          label="Example prompt: broken preview"
          code={`Use OG Pilot. Call debug_open_graph on https://example.com/pricing.\nSummarize the score, the og:image URL, and the issues.\nIf the image is missing or huge, call compress_image on that image URL with width 1200 and height 630.\nDo not call generate_og_image unless I have an active plan.`}
        />
        <p className="text-muted-foreground mt-6 text-base leading-7">
          <strong className="text-gray-900 dark:text-white">Read a brand palette, then generate a card.</strong>{" "}
          <code>extract_color_palette</code> returns hex colors and a text/background pair.{" "}
          <code>generate_og_image</code> does not accept those hex values. It accepts a page URL and an optional catalog
          style. Use the palette to choose a style key, or to check contrast. It does not paint a custom brand color
          into the image.
        </p>
        <CodeSample
          label="Example prompt: palette, then a card"
          code={`Use OG Pilot. Call extract_color_palette on https://example.com/logo.png with limit 5.\nTell me the hex colors and the text/background pair.\nThen call generate_og_image on https://example.com with style modern.\nDo not invent a color argument. The generator has no hex input.`}
        />
      </section>

      <section id="api" className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
          The same image from the REST API
        </h2>
        <p className="text-muted-foreground mt-4 text-base leading-7">
          If the caller is your server, skip MCP and sign a JWT. Required claims are <code>iss</code> (a verified
          domain) and <code>sub</code> (the API key prefix). Sign with HS256 using the API key as the secret. Field
          reference and SDK samples are in the{" "}
          <Link href="/docs" className={linkClass}>
            API docs
          </Link>
          . Create the key from{" "}
          <Link href="/sign_up" className={linkClass}>
            an account
          </Link>
          . A trial can call this API. It cannot call <code>generate_og_image</code>.
        </p>
        <CodeSample label="API request" code={CURL_SAMPLE} />
      </section>

      <section className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">Troubleshooting</h2>
        <div className="mt-6 space-y-6">
          <article>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              The client says authentication required, or invalid_token
            </h3>
            <p className="text-muted-foreground mt-2 text-sm leading-6 md:text-base">
              <code>POST /mcp</code> without a valid bearer token returns HTTP 401 and <code>invalid_token</code>. The
              access token lasts 1 hour. Reconnect so the client can refresh or run the consent flow again. A denied
              consent page returns <code>access_denied</code>. An unknown client id returns <code>invalid_client</code>.
              A scope other than <code>mcp:generate</code> or <code>offline_access</code> returns{" "}
              <code>invalid_scope</code>.
            </p>
          </article>
          <article>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              generate_og_image returned plan_required
            </h3>
            <p className="text-muted-foreground mt-2 text-sm leading-6 md:text-base">
              The connected session is a guest, the account is on a trial, the subscription is not active, or the credit
              balance is zero. The tool does not start a checkout. Sign in on the consent page with an account that has
              an active plan and at least one credit, then connect again. Plans are listed at{" "}
              <Link href="/#pricing" className={linkClass}>
                /#pricing
              </Link>
              . <code>debug_open_graph</code>, <code>compress_image</code>, and <code>extract_color_palette</code> do
              not return this error for a guest.
            </p>
          </article>
          <article>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">The tool returned timeout</h3>
            <p className="text-muted-foreground mt-2 text-sm leading-6 md:text-base">
              For <code>generate_og_image</code>, timeout means the first preview was not stored within about 50
              seconds. The job keeps running. Retry the same URL. A retry inside 30 minutes reuses that generation.
              Debug stops around 12 seconds, palette around 12, and compress around 15. Those timeouts mean that call
              itself stopped, not that a preview is still rendering.
            </p>
          </article>
          <article>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">rate_limited, or a tool is missing</h3>
            <p className="text-muted-foreground mt-2 text-sm leading-6 md:text-base">
              Generation is capped at 10 images per hour, and that path shares 60 requests per hour with protocol calls.
              The three free tools share a separate 40 requests per hour, plus their own caps, so they cannot spend the
              generation budget. If a tool you expect is absent, reconnect. The server does not push{" "}
              <code>tools/list</code> changes.
            </p>
          </article>
          <article>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">invalid_url or a private address</h3>
            <p className="text-muted-foreground mt-2 text-sm leading-6 md:text-base">
              Pass a public <code>http</code> or <code>https</code> URL. Localhost, link-local, and metadata addresses
              are rejected. <code>compress_image</code> and <code>extract_color_palette</code> need an image URL, not a
              base64 string and not an HTML page.
            </p>
          </article>
        </div>
        <p className="text-muted-foreground mt-6 text-sm leading-6">
          Connecting the server stores OAuth client and token metadata, the URLs you submit, and the generated or
          compressed assets. Details are in the{" "}
          <Link href="/privacy" className={linkClass}>
            privacy policy
          </Link>
          .
        </p>
      </section>

      <ActivationGuide
        title="Install MCP, create an API key, or pick a plan"
        links={[
          {
            href: "/docs/mcp#install",
            label: "Install MCP",
            detail: "Add https://ogpilot.com/mcp as a remote Streamable HTTP server and approve OAuth.",
          },
          {
            href: "/sign_up",
            label: "Create an API key",
            detail: "Sign JWTs on your server when the caller is your app, not an agent.",
          },
          {
            href: "/#pricing",
            label: "Pick a plan",
            detail: "generate_og_image needs an active plan and a credit. The tool will not open checkout.",
          },
          {
            href: "/docs",
            label: "API docs",
            detail: "JWT claims, templates, and SDK samples for POST /api/v1/images.",
          },
          {
            href: "/open-graph-debugger",
            label: "Open Graph debugger",
            detail: "The same inspection as debug_open_graph, in the browser.",
          },
          {
            href: "/new",
            label: "Generate one image",
            detail: "Paste one URL in the browser when you do not want a connector.",
          },
        ]}
      >
        <p>
          Connect the server, call <code>debug_open_graph</code> on a public URL, then call{" "}
          <code>generate_og_image</code> once the account has an active plan. If the image should be created inside your
          app, use the{" "}
          <Link href="/docs" className={linkClass}>
            API docs
          </Link>{" "}
          instead of MCP.
        </p>
      </ActivationGuide>
    </div>
  );
}
